import React, { Component } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import intlShape from '@/shared/utils/intlShape';
import Button from '@/shared/components/Button';
import classNames from 'classnames';
import debounce from 'lodash-es/debounce';
import api from '@/shared/api';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import enumCache from '@/shared/services/EnumCacheService';
import { isBeforeMinimum, validateMinimumDate } from '@/shared/utils/validationHelpers';
import DatePicker from '@/shared/components/input/DatePicker';
import Tag from '@/shared/components/Tag';
import RelationListPicker from '@/shared/components/input/RelationListPicker';
import FileUpload from '@/shared/components/input/FileUpload';
import DocumentService from '@/shared/services/DocumentService';
import config from '../../../config';

const messages = defineMessages({
  DocumentDateLabel: 'Document date',

  StartTypingToSearch: 'Start typing to search',
  Success: 'Success',
  Tags: 'Tags',
  EnterTag: 'Enter tag',
  Relation: 'Relation',
  Relations: 'Relations',
  AddRelation: 'Add relation',
  RemoveRelation: 'Remove relation',
  DocumentType: 'Document type',
  Status: 'Status',
  Signature: 'Signature',
  AdvancedUpload: 'Advanced upload',
  Close: 'Close',
});

class AdvancedUploadModal extends Component {
  static propTypes = {
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      newTag: '',
      tags: [],
      availableTags: [],
      relations: [],
      documentTypes: [],
      showValidation: false,
      touched: false,
    };
  }

  componentDidMount() {
    this.initializeModal();
  }

  initializeModal = () => {
    const { intl } = this.props;
    const documentTypesPromise = enumCache.getTranslated('documentType', intl);
    const tagsPromise = api.base.get(`${config.appUrls.documents}/api/Tag`);

    const forbiddenDocumentTypesPromise = DocumentService.getForbiddenDocumentTypes();

    Promise.all([tagsPromise, documentTypesPromise, forbiddenDocumentTypesPromise]).then(
      ([{ data }, documentTypes, forbiddenTypes]) => {
        this.setState({
          availableTags: data,
          documentTypes: documentTypes.filter(x => !forbiddenTypes.includes(x.id)),
        });
      },
    );
  };

  addTagToDocument = tagName => {
    if (!tagName) return;
    const { tags } = this.state;
    const currentDocumentTagNames = tags.map(x => x.name);

    if (currentDocumentTagNames.includes(tagName)) return;

    this.findOrCreateTag(tagName).then(tag => {
      this.setState({
        tags: [...tags, tag],
        touched: true,
      });
    });
  };

  onRemoveTag = tag => {
    const { tags } = this.state;

    this.setState({
      tags: tags.filter(x => x.id !== tag.id),
      touched: true,
    });
  };

  findOrCreateTag = tagName =>
    new Promise((resolve, reject) => {
      const { availableTags } = this.state;
      const tag = availableTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
      if (tag) {
        resolve(tag);
      } else {
        api.base.post(`${config.appUrls.documents}/api/Tag`, { name: tagName }).then(({ data }) => {
          this.setState(
            state => ({
              availableTags: [...state.tags, data],
            }),
            () => resolve(data),
          );
        }, reject);
      }
    });

  addRelation = relation => {
    if (relation && relation.id) {
      this.setState(state => ({
        relations: [...state.relations.filter(x => x.id !== relation.id), relation],
        touched: true,
      }));
    }
  };

  removeRelation = relation => {
    if (relation && relation.id) {
      this.setState(state => ({
        relations: state.relations.filter(x => x.id !== relation.id),
        touched: true,
      }));
    }
  };

  // debounce makes sure that if this method is called again within 300 ms, only the last call is executed.
  // this method adds the tag automatically to the document when it is found in the list of tags
  // this is needed because if there are two tags: test and test2, and the user starts typing, test2 will never be able
  // to be added to the document because the value that was entered will me immediately processed and found
  debouncedFindAndAddTag = debounce(value => {
    const { availableTags } = this.state;
    if (value && availableTags.find(x => x.name === value)) {
      this.addTagToDocument(value);
      this.setState({ newTag: '' });
    }
  }, 400);

  onTagValueChanged = value => {
    this.setState({ newTag: value, touched: true });
    this.debouncedFindAndAddTag(value);
  };

  selectedDocumentTypeChanged = item => {
    this.setState({
      documentType: item,
      touched: true,
    });
  };

  showValidation = () => {
    this.setState({ showValidation: true });
  };

  isFormInvalid = () => {
    const { documentDate } = this.state;
    if (!documentDate) return true;
    if (!validateMinimumDate(documentDate)) return true;

    return false;
  };

  render() {
    const { onClose, intl } = this.props;
    const {
      documentDate,
      dateFocused,
      availableTags,
      newTag,
      documentType,
      documentTypes,
      showValidation,
      signingStatus,
      relations,
      tags,
      touched,
    } = this.state;

    let documentUploadEndpoint = `${config.appUrls.documents}/api/documents`; // -1 = document type unknown, used for recognizing

    const desiredDocumentType = documentType ? documentType.id : -1;
    documentUploadEndpoint += `?documentType=${desiredDocumentType}&source=300`;

    if (signingStatus) {
      documentUploadEndpoint += `&signingStatus=${signingStatus.id}`;
    }

    if (documentDate) {
      documentUploadEndpoint += `&documentDate=${documentDate.format('YYYY-MM-DD')}`;
    }
    if (tags && tags.length > 0) {
      documentUploadEndpoint += `&tags=${tags.map(x => x.id).join(';')}`;
    }

    if (relations && relations.length > 0) {
      if (desiredDocumentType !== -1) {
        // set document as processed if its not gonna be recognized
        documentUploadEndpoint += `&recognitionStatus=4`;
      }

      documentUploadEndpoint += `&${relations
        .map(x => x.id)
        .map((n, index) => `relationIds[${index}]=${n}`)
        .join('&')}`;
    }

    const signingStatuses = [1, 2, 10, 20].map(x => ({
      id: x,
      label: intl.formatMessage(listItemMsg('signingStatus', x)),
    }));

    return (
      <Modal show onHide={onClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage {...messages.AdvancedUpload} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {
            <div className="content-wrapper">
              <div className="document-info">
                <form ref={this.form}>
                  <div className="form-horizontal">
                    <div
                      className={classNames('form-group', {
                        'has-error': showValidation && !documentDate,
                      })}
                    >
                      <label className="col-sm-4 col-md-4 ">
                        <FormattedMessage {...messages.DocumentDateLabel} />
                      </label>
                      <div className="col-sm-6 col-md-8 ">
                        <DatePicker
                          placeholder={intl.formatMessage(messages.DocumentDateLabel)}
                          date={documentDate}
                          onDateChange={value =>
                            this.setState({ documentDate: value, touched: true })
                          }
                          focused={dateFocused}
                          disabled={false}
                          onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                          id="documentDate"
                          required
                          block
                          small
                          isOutsideRange={isBeforeMinimum}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="col-sm-4 col-md-4 margin5T">
                        <FormattedMessage {...messages.DocumentType} />
                      </label>
                      <div className="col-sm-6 col-md-8 margin5T">
                        <AutoComplete
                          disabled={false}
                          items={documentTypes}
                          placeholder={intl.formatMessage(messages.StartTypingToSearch)}
                          onChange={item => this.selectedDocumentTypeChanged(item)}
                          mode="edit"
                          value={documentType}
                          showAll
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="col-sm-4 col-md-4 margin5T">
                        <FormattedMessage {...messages.Signature} />
                      </label>
                      <div className="col-sm-6 col-md-8 margin5T">
                        <AutoComplete
                          disabled={false}
                          items={signingStatuses}
                          placeholder={intl.formatMessage(messages.Signature)}
                          onChange={item => this.setState({ touched: true, signingStatus: item })}
                          mode="edit"
                          value={signingStatus}
                          showAll
                        />
                        {/*
                        <select
                          disabled={false}
                          className="form-control"
                          onChange={e => {
                            const status = e.target.value;
                            this.setState({ signingStatus: status, touched: true });
                          }}
                          value={signingStatus}
                        >
                          <option value="1">
                            {intl.formatMessage(listItemMsg('signingStatus', '1'))}
                          </option>
                          <option value="2">
                            {intl.formatMessage(listItemMsg('signingStatus', '2'))}
                          </option>
                          <option value="10">
                            {intl.formatMessage(listItemMsg('signingStatus', '10'))}
                          </option>
                          <option value="20">
                            {intl.formatMessage(listItemMsg('signingStatus', '20'))}
                          </option>
                        </select> */}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="col-sm-4 col-md-4 margin5T">
                        <FormattedMessage {...messages.Relations} />
                      </label>
                      <div className="col-sm-6 col-md-8">
                        <RelationListPicker
                          relations={relations || []}
                          onAddRelation={this.addRelation}
                          onRemoveRelation={this.removeRelation}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="col-sm-4 col-md-4 margin5T">
                        <FormattedMessage {...messages.Tags} />
                      </label>
                      <div className="col-sm-6 col-md-8">
                        <div className={classNames({ margin20B: tags && tags.length > 0 })}>
                          <input
                            type="text"
                            disabled={false}
                            maxLength="200"
                            className="form-control tag-input"
                            value={newTag}
                            placeholder={intl.formatMessage(messages.EnterTag)}
                            onChange={e => this.onTagValueChanged(e.target.value.replace(';', ''))}
                            list="tags"
                            autoComplete="off"
                            onKeyDown={e => {
                              if (e.keyCode === 13) {
                                this.addTagToDocument(newTag);
                                this.setState({ newTag: '' });
                              } else if (e.keyCode === 27) {
                                this.setState({ newTag: '' });
                              }
                            }}
                          />
                        </div>
                        <div>
                          {tags &&
                            tags.map(item => (
                              <Tag key={`Tag-${item.id}`} onRemove={this.onRemoveTag} item={item} />
                            ))}
                        </div>
                      </div>
                    </div>

                    {touched && (
                      <div className="form-group">
                        <div className="col-sm-6 col-md-8 col-sm-offset-6 col-md-offset-4">
                          <div className="file-upload-wrapper">
                            <FileUpload
                              endpoint={documentUploadEndpoint}
                              removeSuccessfullAfter={5000}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
              <datalist id="tags">
                {availableTags && availableTags.map(t => <option key={t.id} value={t.name} />)}
              </datalist>
            </div>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose}>{intl.formatMessage(messages.Close)}</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default injectIntl(AdvancedUploadModal);
